
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { saveAppointment, getAppointmentById } from '@/services/medicalService';
import { MedicalRecordLimitBanner } from '@/components/medical/MedicalRecordLimitBanner';
import { useMedicalRecordLimits } from '@/hooks/useMedicalRecordLimits';
import { useTranslation } from 'react-i18next';

// Time options for appointment selection
const timeOptions = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30"
];

export default function AddEditAppointmentForm() {
  const { t } = useTranslation();
  const { petId, appointmentId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = appointmentId !== 'new';
  const { hasReachedLimit } = useMedicalRecordLimits(petId);

  const [formData, setFormData] = useState({
    appointment_type: '',
    appointment_date: new Date(),
    appointment_time: "09:00",
    status: 'scheduled',
    vet_name: '',
    vet_phone: '',
    location: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && appointmentId) {
      setLoading(true);
      getAppointmentById(appointmentId)
        .then(data => {
          if (data) {
            const appointmentDate = new Date(data.appointment_datetime);
            const hours = appointmentDate.getHours().toString().padStart(2, '0');
            const minutes = appointmentDate.getMinutes().toString().padStart(2, '0');
            
            setFormData({
              appointment_type: data.appointment_type || '',
              appointment_date: appointmentDate,
              appointment_time: `${hours}:${minutes}`,
              status: data.status,
              vet_name: data.vet_name || '',
              vet_phone: data.vet_phone || '',
              location: data.location || '',
              notes: data.notes || ''
            });
          }
        })
        .catch(err => {
          setError(t('medical.loadAppointmentError'));
          toast({
            title: t('common.error'),
            description: t('medical.loadAppointmentError'),
            variant: "destructive"
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [appointmentId, isEditing, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      setFormData(prev => ({ ...prev, appointment_date: date }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.appointment_type.trim()) {
      setError(t('medical.appointmentTypeRequired'));
      setLoading(false);
      return;
    }

    // Check if user has reached limit (only for new records)
    if (!isEditing && hasReachedLimit) {
      setError(t('medical.limitReached'));
      toast({
        title: t('medical.limitReachedTitle'),
        description: t('medical.limitReached'),
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    // Create appointment datetime from date and time
    const [hours, minutes] = formData.appointment_time.split(':');
    const appointmentDateTime = new Date(formData.appointment_date);
    appointmentDateTime.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

    try {
      await saveAppointment(
        petId as string,
        {
          ...formData,
          appointment_datetime: appointmentDateTime
        },
        isEditing ? appointmentId : undefined
      );

      toast({
        title: isEditing ? t('medical.appointmentUpdated') : t('medical.appointmentScheduled'),
        description: t('medical.appointmentSaved'),
      });

      navigate(`/medical/${petId}/appointments`);
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to save appointment";
      setError(errorMessage);
      toast({
        title: t('common.error'),
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return <div className="p-4 text-center">{t('medical.loadingAppointment')}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <form onSubmit={handleSubmit} className="space-y-6">
        {!isEditing && petId && (
          <MedicalRecordLimitBanner petId={petId} type={hasReachedLimit ? 'limit' : 'warning'} />
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="appointment_type">{t('medical.appointmentForm.appointmentType')}</Label>
          <Input
            id="appointment_type"
            name="appointment_type"
            value={formData.appointment_type}
            onChange={handleInputChange}
            placeholder="e.g., Check-up, Vaccination"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="appointment_date">{t('medical.appointmentForm.date')}</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !formData.appointment_date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.appointment_date ? format(formData.appointment_date, "PPP") : t('medical.form.selectDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={formData.appointment_date}
                onSelect={handleDateChange}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label htmlFor="appointment_time">{t('medical.appointmentForm.time')}</Label>
          <Select
            value={formData.appointment_time}
            onValueChange={(value) => handleSelectChange('appointment_time', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('medical.appointmentForm.selectTime')} />
            </SelectTrigger>
            <SelectContent>
              {timeOptions.map(time => (
                <SelectItem key={time} value={time}>{time}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isEditing && (
          <div className="space-y-2">
            <Label htmlFor="status">{t('medical.appointmentForm.status')}</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => handleSelectChange('status', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">{t('medical.status.scheduled')}</SelectItem>
                <SelectItem value="confirmed">{t('medical.status.confirmed')}</SelectItem>
                <SelectItem value="completed">{t('medical.status.completed')}</SelectItem>
                <SelectItem value="cancelled_by_user">{t('medical.status.cancelledByUser')}</SelectItem>
                <SelectItem value="cancelled_by_vet">{t('medical.status.cancelledByVet')}</SelectItem>
                <SelectItem value="rescheduled">{t('medical.status.rescheduled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="vet_name">{t('medical.form.veterinarianName')}</Label>
          <Input
            id="vet_name"
            name="vet_name"
            value={formData.vet_name}
            onChange={handleInputChange}
            placeholder="Dr. Smith"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="vet_phone">{t('medical.appointmentForm.vetPhone')}</Label>
          <Input
            id="vet_phone"
            name="vet_phone"
            value={formData.vet_phone}
            onChange={handleInputChange}
            placeholder="(123) 456-7890"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="location">{t('medical.appointmentForm.location')}</Label>
          <Input
            id="location"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            placeholder="Pet Clinic Address"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">{t('medical.form.notes')}</Label>
          <Textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder={t('medical.appointmentForm.notesPlaceholder')}
            rows={3}
          />
        </div>

        <Button
          type="submit"
          className="w-full bg-pet-purple hover:bg-pet-purple/90"
          disabled={loading || (!isEditing && hasReachedLimit)}
        >
          {loading ? t('common.saving') : isEditing ? t('medical.appointmentUpdated') : t('medical.scheduleAppointment')}
        </Button>
      </form>
    </div>
  );
}
